import type { PropertyDefinition, PropertyValue } from "@shared/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { RemoveOptionConfirmDialog } from "@/components/remove-option-confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePropertyOptions } from "@/hooks/use-property-options";
import { NAMED_COLORS } from "@/lib/property-colors";
import { rpcClient } from "@/lib/rpc";

interface PropertyValueCellProps {
  definition: PropertyDefinition;
  value: PropertyValue | undefined;
  applicationId: string;
  jobQueryKey: unknown[];
  readOnly?: boolean;
}

export function PropertyValueCell({
  definition,
  value,
  applicationId,
  jobQueryKey,
  readOnly = false,
}: PropertyValueCellProps) {
  const [open, setOpen] = useState(false);
  const [textEditing, setTextEditing] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const upsertMutation = useMutation({
    mutationFn: (newValue: string | string[] | null) =>
      rpcClient.applications.upsertPropertyValue({
        id: applicationId,
        defId: definition.id,
        value: newValue,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobQueryKey });
    },
  });

  const {
    addOption: addOptionMutation,
    updateOption: updateOptionMutation,
    deleteOption: deleteOptionMutation,
    pendingRemoval,
    confirmRemoval,
    cancelRemoval,
  } = usePropertyOptions(definition, jobQueryKey);

  const currentValue = value?.value;

  function renderDisplay() {
    if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
      return (
        <span className="text-muted-foreground text-xs italic">{readOnly ? "—" : "Vazio"}</span>
      );
    }
    if (definition.type === "text") {
      return <span className="text-sm">{currentValue as string}</span>;
    }
    if (definition.type === "select") {
      const opt = definition.options?.find((o) => o.id === currentValue);
      if (!opt) return <span className="text-muted-foreground text-xs italic">—</span>;
      return (
        <Badge variant="secondary" style={{ backgroundColor: opt.color }}>
          {opt.label}
        </Badge>
      );
    }
    if (definition.type === "multi_select") {
      const selectedIds = currentValue as string[];
      const selected = definition.options?.filter((o) => selectedIds.includes(o.id)) ?? [];
      return (
        <div className="flex flex-wrap gap-1">
          {selected.map((o) => (
            <Badge key={o.id} variant="secondary" style={{ backgroundColor: o.color }}>
              {o.label}
            </Badge>
          ))}
        </div>
      );
    }
    return null;
  }

  if (readOnly) return <>{renderDisplay()}</>;

  // Text type: inline edit (no popover)
  if (definition.type === "text") {
    if (textEditing) {
      return (
        <Input
          key={(currentValue as string) ?? ""}
          defaultValue={(currentValue as string) ?? ""}
          autoFocus
          className="h-7 text-sm"
          onBlur={(e) => {
            upsertMutation.mutate(e.target.value || null);
            setTextEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              upsertMutation.mutate(e.currentTarget.value || null);
              setTextEditing(false);
            }
            if (e.key === "Escape") setTextEditing(false);
          }}
        />
      );
    }
    return (
      <button
        type="button"
        className="hover:bg-muted rounded px-1 text-left"
        onClick={() => setTextEditing(true)}
      >
        {renderDisplay()}
      </button>
    );
  }

  // Select / multi_select: popover anchored to the value display
  const isMulti = definition.type === "multi_select";
  const selectedIds = isMulti
    ? ((currentValue as string[]) ?? [])
    : currentValue
      ? [currentValue as string]
      : [];

  function toggleOption(optionId: string) {
    if (isMulti) {
      const current = (currentValue as string[]) ?? [];
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      upsertMutation.mutate(next.length > 0 ? next : null);
    } else {
      const isSame = currentValue === optionId;
      upsertMutation.mutate(isSame ? null : optionId);
      setOpen(false);
    }
  }

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) setEditingOptionId(null);
        }}
      >
        <PopoverTrigger asChild>
          <button type="button" className="hover:bg-muted rounded px-1 text-left inline-flex">
            {renderDisplay()}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start" side="bottom">
          <div className="flex flex-col gap-1">
            {definition.options?.map((opt) => (
              <div key={opt.id} className="group flex items-center gap-1 rounded hover:bg-muted">
                <button
                  type="button"
                  className="flex flex-1 items-center gap-2 px-2 py-1 text-sm text-left"
                  onClick={() => toggleOption(opt.id)}
                >
                  {isMulti && (
                    <Checkbox
                      checked={selectedIds.includes(opt.id)}
                      onCheckedChange={() => {}}
                      className="pointer-events-none"
                    />
                  )}
                  <Badge variant="secondary" style={{ backgroundColor: opt.color }}>
                    {opt.label}
                  </Badge>
                </button>
                <Popover
                  open={editingOptionId === opt.id}
                  onOpenChange={(isOpen) => setEditingOptionId(isOpen ? opt.id : null)}
                >
                  <PopoverTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-52 p-2" side="right" align="start">
                    <Input
                      key={opt.id}
                      defaultValue={opt.label}
                      autoFocus
                      className="h-7 text-sm mb-2"
                      onBlur={(e) => {
                        const newLabel = e.target.value.trim();
                        if (newLabel && newLabel !== opt.label) {
                          updateOptionMutation.mutate({ optionId: opt.id, label: newLabel });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const newLabel = e.currentTarget.value.trim();
                          if (newLabel && newLabel !== opt.label) {
                            updateOptionMutation.mutate({ optionId: opt.id, label: newLabel });
                          }
                          setEditingOptionId(null);
                        }
                        if (e.key === "Escape") setEditingOptionId(null);
                      }}
                    />
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-2 py-1 rounded hover:bg-muted text-sm text-destructive"
                      onClick={() =>
                        deleteOptionMutation.mutate(
                          { optionId: opt.id },
                          {
                            onSuccess: () => setEditingOptionId(null),
                          },
                        )
                      }
                    >
                      <Trash2 className="h-3 w-3" />
                      Excluir
                    </button>
                    <p className="text-xs text-muted-foreground px-2 mt-2 mb-1">Cores</p>
                    {NAMED_COLORS.map((c) => (
                      <button
                        type="button"
                        key={c.value}
                        className="flex w-full items-center gap-2 px-2 py-1 rounded hover:bg-muted text-sm"
                        onClick={() =>
                          updateOptionMutation.mutate({ optionId: opt.id, color: c.value })
                        }
                      >
                        <span
                          className="h-4 w-4 rounded-sm flex-shrink-0 border border-border"
                          style={{ backgroundColor: c.value }}
                        />
                        {c.name}
                        {opt.color === c.value && <Check className="h-3 w-3 ml-auto" />}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              </div>
            ))}
            <div className="border-t mt-1 pt-1 flex gap-1">
              <Input
                placeholder="Adicionar opção"
                value={newOptionLabel}
                onChange={(e) => setNewOptionLabel(e.target.value)}
                className="h-7 text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newOptionLabel.trim()) {
                    addOptionMutation.mutate(newOptionLabel.trim(), {
                      onSuccess: () => setNewOptionLabel(""),
                    });
                  }
                }}
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                disabled={!newOptionLabel.trim() || addOptionMutation.isPending}
                onClick={() => {
                  if (newOptionLabel.trim()) {
                    addOptionMutation.mutate(newOptionLabel.trim(), {
                      onSuccess: () => setNewOptionLabel(""),
                    });
                  }
                }}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <RemoveOptionConfirmDialog
        pending={pendingRemoval}
        onConfirm={confirmRemoval}
        onCancel={cancelRemoval}
        isPending={deleteOptionMutation.isPending}
      />
    </>
  );
}
